/**
 * AssetManifest.ts - Central registry for all game assets
 * Defines spritesheets, animations, and loading configuration
 */

export interface SpritesheetDefinition {
  name: string;
  url: string;
  frameWidth: number;
  frameHeight: number;
}

export interface AnimationDefinition {
  frames: number[];
  frameTime: number;
  loop?: boolean;
}

export interface CharacterAnimations {
  idle: AnimationDefinition;
  walkDown: AnimationDefinition;
  walkUp: AnimationDefinition;
  walkLeft: AnimationDefinition;
  walkRight: AnimationDefinition;
}

export const ASSET_MANIFEST = {
  // Sprite sheets to load
  spritesheets: [
    {
      name: 'world_tileset',
      url: '/assets/sprites/world_tileset.png',
      frameWidth: 16,
      frameHeight: 16
    },
    {
      name: 'characters',
      url: '/assets/sprites/characters.png',
      frameWidth: 16,
      frameHeight: 16
    },
    {
      name: 'enemies',
      url: '/assets/sprites/enemies.png',
      frameWidth: 32,
      frameHeight: 32
    },
    {
      name: 'effects',
      url: '/assets/sprites/effects.png',
      frameWidth: 16,
      frameHeight: 16
    },
    {
      name: 'ui',
      url: '/assets/sprites/ui.png',
      frameWidth: 8,
      frameHeight: 8
    },
  ] as SpritesheetDefinition[],

  // Player character animations (frame indices into characters.png)
  // Layout: 12 frames per character class
  // Row 0: Fighter walk cycle (down, up, left, right - 3 frames each)
  // Row 1: Thief walk cycle
  // Row 2: Monk walk cycle
  // Row 3: Red Mage walk cycle
  // Row 4: White Mage walk cycle
  // Row 5: Black Mage walk cycle
  playerAnimations: {
    // Each direction has: idle, step1, step2 (3 frames)
    walkDown: { frames: [0, 1, 0, 2], frameTime: 150, loop: true },
    walkUp: { frames: [3, 4, 3, 5], frameTime: 150, loop: true },
    walkLeft: { frames: [6, 7, 6, 8], frameTime: 150, loop: true },
    walkRight: { frames: [9, 10, 9, 11], frameTime: 150, loop: true },
    idle: { frames: [0], frameTime: 500, loop: false },
  } as Record<string, AnimationDefinition>,

  // Character class sprite row offsets (in characters.png)
  characterClassRows: {
    FIGHTER: 0,
    THIEF: 1,
    MONK: 2,
    RED_MAGE: 3,
    WHITE_MAGE: 4,
    BLACK_MAGE: 5,
    // Upgraded classes
    KNIGHT: 6,
    NINJA: 7,
    MASTER: 8,
    RED_WIZARD: 9,
    WHITE_WIZARD: 10,
    BLACK_WIZARD: 11,
  } as Record<string, number>,

  // Battle effects animations (frame indices into effects.png)
  battleEffects: {
    SLASH: { frames: [0, 1, 2], frameTime: 80, loop: false },
    FIRE: { frames: [3, 4, 5, 6], frameTime: 100, loop: false },
    ICE: { frames: [7, 8, 9, 10], frameTime: 100, loop: false },
    LIGHTNING: { frames: [11, 12, 13], frameTime: 60, loop: false },
    HEAL: { frames: [14, 15, 16, 17], frameTime: 120, loop: false },
    CRITICAL: { frames: [18, 19, 20, 21], frameTime: 60, loop: false },
    MISS: { frames: [22], frameTime: 300, loop: false },
    POISON: { frames: [23, 24, 25], frameTime: 150, loop: true },
    DEATH: { frames: [26, 27, 28, 29], frameTime: 150, loop: false },
  } as Record<string, AnimationDefinition>,
};

// Get frames per row in character sheet (12 frames: 3 frames x 4 directions)
export const FRAMES_PER_CHARACTER_ROW = 12;

// Get base frame index for a character class
export function getCharacterBaseFrame(characterClass: string): number {
  const row = ASSET_MANIFEST.characterClassRows[characterClass] ?? 0;
  return row * FRAMES_PER_CHARACTER_ROW;
}

// Get animation frames for a character class and direction
export function getCharacterAnimation(
  characterClass: string,
  direction: 'walkDown' | 'walkUp' | 'walkLeft' | 'walkRight' | 'idle'
): number[] {
  const baseFrame = getCharacterBaseFrame(characterClass);
  const animation = ASSET_MANIFEST.playerAnimations[direction];
  if (!animation) return [baseFrame];
  return animation.frames.map(f => baseFrame + f);
}
